/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

/* eslint-disable @typescript-eslint/naming-convention */
import type { OAuth2Result, OAuth2Error } from '@veeam-vspc/models/rest';

import { TokenManagerConfig } from './entries';
import { getEmptyToken, isAccessTokenValid } from './utils';
import { TransportService, TransportStrategies, HttpHeaders, TimeUnitsInMs, isErrorThatNeedToBeShown } from '../../core';
import { GrantTypes, TokenSupplier } from '../../suppliers/rest';
import { tokenManagerTransportStrategy } from '../../strategies';
import { LoginSupplier } from '../../suppliers/web';

import type { RequestErrorResponse } from '../../interfaces';
import type { TokenData } from '../../suppliers/rest';
import type { BearerToken, TokenManagerErrorResponse, TokenManagerResponse, TokenManagerServiceConfig } from './interfaces';

export class TokenManagerService {

    protected readonly config: TokenManagerConfig;
    protected readonly tokenSupplier: TokenSupplier;
    protected readonly loginSupplier: LoginSupplier;

    protected refreshPromise: Promise<void> | null = null;

    constructor(tokenManagerServiceConfig: TokenManagerServiceConfig<RequestErrorResponse | Error>) {
        const {
            commands,
            httpRequestService,
            transportService,
            restPrefix = '',
            baseUrl = '',
            ...tokenManagerConfigParams
        } = tokenManagerServiceConfig;

        this.config = new TokenManagerConfig(tokenManagerConfigParams);
        this.loginSupplier = new LoginSupplier(transportService);
        this.tokenSupplier = new TokenSupplier(new TransportService<OAuth2Error | Error>({
            httpRequestService,
            strategies: {
                [TransportStrategies.RestServer]: tokenManagerTransportStrategy,
            },
            customBaseHeaders: {
                [HttpHeaders.ContentType]: 'application/x-www-form-urlencoded',
            },
            restPrefix,
            baseUrl,
            commands,
        }));

        this.config.setOnTokenUpdateHandler(() => {
            const bearerToken: BearerToken | null = this.config.getBearerToken();

            if (this.config.getTokenTimerId() && ((bearerToken && bearerToken.logout) || !bearerToken)) {
                this.config.resetAutoLogout();
                this.config.removeBearerToken();

                return commands.showAuthError();
            }
        });
    }

    checkAuthorization(): Promise<void> {
        const bearerToken: BearerToken | null = this.config.getBearerToken();
        const { access_token, refresh_token, access_expires_in, refresh_expires_in } = bearerToken || {};

        if (this.refreshPromise) {
            return this.refreshPromise
                .then(() => this.checkAuthorization());
        } else if (bearerToken && access_token && isAccessTokenValid(access_expires_in)) {
            return Promise.resolve();
        } else if (bearerToken && access_token && refresh_token && this.config.isRefreshTokenValid(refresh_expires_in)) {
            return this.refreshToken()
                .then(() => this.checkAuthorization())
                .catch((err) => {
                    isErrorThatNeedToBeShown(err) && console.error(err);
                    return Promise.reject();
                });
        }

        this.config.removeBearerToken();

        return Promise.reject();
    }

    login(username: string, password: string) {
        return this.sendRequest({
            grant_type: GrantTypes.Password,
            username,
            password,
        });
    }

    mfa(mfa_token: string, mfa_code: string) {
        return this.sendRequest({
            grant_type: GrantTypes.Mfa,
            mfa_token,
            mfa_code,
        });
    }

    authorizationCode(code: string) {
        return this.sendRequest({
            grant_type: GrantTypes.AuthorizationCode,
            code,
        });
    }

    refreshToken(): Promise<void> {
        const bearerToken: BearerToken | null = this.config.getBearerToken();
        const refreshToken: string | null = bearerToken && bearerToken.refresh_token ? bearerToken.refresh_token : null;

        if (this.refreshPromise) {
            return this.refreshPromise;
        } else if (refreshToken && bearerToken && this.config.isRefreshTokenValid(bearerToken.refresh_expires_in)) {
            this.config.removeBearerToken();

            return this.refreshPromise = this.getNewAccessToken(refreshToken)
                .then(() => {})
                .finally(() => {
                    if (this.refreshPromise) {
                        this.refreshPromise = null;
                    }
                });
        }

        this.config.removeBearerToken();

        return Promise.reject();
    }

    logout(): Promise<void> {
        return Promise.resolve(this.config.getLogoutUrl())
            .then((logoutUrl: string | null) => {
                if (logoutUrl) {
                    return this.loginSupplier.logout(logoutUrl)
                        .catch((err) => {
                            isErrorThatNeedToBeShown(err) && console.error(err);
                        });
                }
            })
            .then(() => {
                this.config.resetAutoLogout();
                this.config.removeBearerToken(true);
            });
    }

    getAccessToken(): Promise<string | null> {
        const bearerToken: BearerToken | null = this.config.getBearerToken();
        const { access_token, refresh_token, access_expires_in, refresh_expires_in } = bearerToken || {};

        if (this.refreshPromise) {
            return this.refreshPromise
                .then(() => this.getAccessToken())
                .catch(() => {
                    this.config.removeBearerToken();
                    return null;
                });
        } else if (bearerToken && access_token && isAccessTokenValid(access_expires_in)) {
            return Promise.resolve(access_token);
        } else if (bearerToken && access_token && refresh_token && this.config.isRefreshTokenValid(refresh_expires_in)) {
            return this.refreshToken()
                .then(() => this.getAccessToken())
                .catch(() => {
                    this.config.removeBearerToken();
                    return null;
                });
        }

        this.config.removeBearerToken();
        return Promise.resolve(null);
    }

    startTimer(sessionExpirationTimeInSeconds?: number): void {
        this.config.startTimer(sessionExpirationTimeInSeconds);
    }

    setLastActiveTime(): void {
        this.config.setLastActionTime();
    }

    protected getNewAccessToken(refresh_token: string) {
        return this.sendRequest({
            grant_type: GrantTypes.RefreshToken,
            refresh_token,
        });
    }

    protected sendRequest(data: TokenData) {
        return this.tokenSupplier.token(this.config.getAuthUrl(), data, { notShowDefaultError: true })
            .then((dataResponse: OAuth2Result) => this.handleTokenResponse(dataResponse))
            .catch((errorResponse: OAuth2Error | Error) => this.handleTokenErrorResponse(errorResponse));
    }

    protected handleTokenResponse(dataResponse: OAuth2Result): TokenManagerResponse {
        const { access_token, refresh_token, expires_in, mfa_token } = dataResponse;

        if (access_token) {
            const expireTimeInMs = expires_in * TimeUnitsInMs.Second;
            const refresh_expires_in = Date.now() + expireTimeInMs;
            const access_expires_in = refresh_expires_in - (expireTimeInMs / this.config.getReduceTokenLiveCoef());

            this.config.setBearerToken({
                access_token,
                refresh_token,
                access_expires_in,
                refresh_expires_in,
            });

            return {
                successAuth: true,
            };
        } else {
            this.config.setBearerToken(getEmptyToken());

            return {
                nextFactor: true,
                mfaToken: mfa_token as string,
            };
        }
    }

    protected handleTokenErrorResponse(errorResponse: OAuth2Error | Error): Promise<TokenManagerErrorResponse> {
        if (errorResponse instanceof Error) {
            return Promise.reject({
                error: errorResponse.name,
                errorDescription: errorResponse.message,
                errorUri: errorResponse.stack,
            });
        }

        const { error, error_description: errorDescription, error_uri: errorUri } = errorResponse as OAuth2Error;

        return Promise.reject({
            error,
            errorDescription,
            errorUri,
        });
    }

}
